import { PencilSquareIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { INVALID_ALL_INPUTS } from 'config'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  downloadFile,
  getPostClosingFields,
  submitPostClosingPDF,
  updatePostClosingFields,
  uploadFiles,
} from 'services'
import Api from 'services/api'
import { ButtonGroup } from 'stories/components'
import { InputConvert, InputValidate, REQUIRED_FIELD_ERROR_MESSAGE } from 'utils'

import { confirmGenerate } from '../ConfirmGenerateDialog'
import { AssignmentPdfSettingsModal } from './assignmentPdfSettingsModal'
import { addressForAssignor, AssignorOption, normalizeAssignorOptions } from './assignorOptions'
import { defaultInputs, inputGroups } from './constants'
import { DeedOfTrust } from './DeedOfTrust'
import { Mortage } from './Mortage'
import { SecurityDeed } from './SecurityDeed'

const assignmentTypeOptions: Record<string, string> = {
  mortage: 'Assignment of Mortgage',
  trust: 'Assignment of Deed Of Trust',
  security: 'Assignment of Security Deed',
}

const assignmentComponents: Record<string, any> = {
  mortage: Mortage,
  trust: DeedOfTrust,
  security: SecurityDeed,
}

const ASSIGNOR_INDEXES = [1, 2, 3, 4, 5, 6, 7] as const

const assignorEntityNameKey = (index: number) => `assignorEntityName${index}`
const assignorOverrideKey = (index: number) => `assignorEntityName${index}Override`

const isAssignorEntityNameKey = (key: string) => /^assignorEntityName[1-7]$/.test(key)

const isOverrideEnabled = (value: any) => value === true || value === 'true' || value === 1 || value === '1'

export default function Assignment(props: any) {
  const [assignmentNames, setAssignmentNames] = useState<AssignorOption[]>([])
  const assignmentNamesRef = useRef<AssignorOption[]>([])
  assignmentNamesRef.current = assignmentNames
  const [inputs, setInputs] = useState(defaultInputs())
  const [download, setDownload] = useState(-1)
  const [assignmentType, setAssignmentType] = useState('mortage')

  const [rulesConfig] = useSelector((state: any) => [state.globalConfigReducer[GlobalConfigType.RulesConfig]])
  const { fileExtensions: acceptedFileTypes } = rulesConfig
  const acceptedFileTypeText = useMemo(
    () =>
      [...(acceptedFileTypes || [])]
        .sort()
        .map((v: string) => `.${v}`)
        .join(', '),
    [acceptedFileTypes],
  )

  const changeAssignmentNames = (inputs: any, options: AssignorOption[]) => {
    const names = options.map((item) => item.name)
    for (let i = 1; i <= 7; i += 1) {
      inputs[`assignorEntityName${i}`].options = names
    }
    return inputs
  }

  const fillAssignorAddress = (source: Record<string, any>, name: string, onlyWhenEmpty = false) => {
    if (!source.assignorAddress) return
    if (onlyWhenEmpty && source.assignorAddress.value) return
    const address = addressForAssignor(assignmentNamesRef.current, name)
    if (onlyWhenEmpty && !address) return
    source.assignorAddress.value = address
    source.assignorAddress.error = InputValidate(source.assignorAddress)
  }

  const getLinkedAssignorValue = (source: Record<string, any>, preferSavedData = false) => {
    const readValue = (index: number) => {
      const key = assignorEntityNameKey(index)
      if (preferSavedData) return source[key]
      return source[key]?.value
    }
    const readOverride = (index: number) => {
      const key = assignorOverrideKey(index)
      if (preferSavedData) return source[key]
      return source[key]?.value
    }

    return (
      ASSIGNOR_INDEXES.filter((index) => !isOverrideEnabled(readOverride(index)))
        .map((index) => readValue(index))
        .find((value) => !!value) ||
      ASSIGNOR_INDEXES.map((index) => readValue(index)).find((value) => !!value) ||
      ''
    )
  }

  useEffect(() => {
    let nInputs = cloneDeep(inputs)
    props.setLoading(true)
    getPostClosingFields('assignment').then((data) => {
      nInputs = changeAssignmentNames(nInputs, assignmentNamesRef.current)
      Object.keys(nInputs).forEach((key) => {
        if (/^assignorEntityName[1-7]Override$/.test(key)) {
          nInputs[key].value = isOverrideEnabled(data[key])
          nInputs[key].error = ''
          return
        }
        nInputs[key].value = data[key] || nInputs[key].value || ''
        nInputs[key].error = InputValidate(nInputs[key])
      })

      const hasAnyOverride = ASSIGNOR_INDEXES.some((index) => isOverrideEnabled(data[assignorOverrideKey(index)]))
      const linkedAssignorName = hasAnyOverride
        ? getLinkedAssignorValue(data, true)
        : ASSIGNOR_INDEXES.map((index) => data[assignorEntityNameKey(index)]).find((value) => !!value) || ''

      for (const index of ASSIGNOR_INDEXES) {
        const fieldKey = assignorEntityNameKey(index)
        const overridden = isOverrideEnabled(data[assignorOverrideKey(index)])
        if (!hasAnyOverride || !overridden) {
          nInputs[fieldKey].value = linkedAssignorName
          nInputs[fieldKey].inputType = 'select'
          nInputs[fieldKey].error = InputValidate(nInputs[fieldKey])
        } else {
          nInputs[fieldKey].inputType = 'text'
          nInputs[fieldKey].type = 'text'
          nInputs[fieldKey].error = InputValidate(nInputs[fieldKey])
        }
      }

      fillAssignorAddress(nInputs, linkedAssignorName, true)
      nInputs.legalFile.acceptFileTypes = acceptedFileTypeText
      setInputs(nInputs)
      props.setLoading(false)
    })

    getPostClosingFields('assignmentNames').then((data) => setAssignmentNames(normalizeAssignorOptions(data)))
  }, [])

  useEffect(() => {
    let nInputs = cloneDeep(inputs)
    nInputs = changeAssignmentNames(nInputs, assignmentNames)
    const linkedName = getLinkedAssignorValue(nInputs)
    fillAssignorAddress(nInputs, linkedName, true)
    setInputs(nInputs)
  }, [assignmentNames])

  const onUpdateAssignmentNames = (newValues: AssignorOption[]) => {
    const next = normalizeAssignorOptions(newValues)
    assignmentNamesRef.current = next
    setAssignmentNames(next)
    updatePostClosingFields('assignmentNames', { assignmentNames: next })
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].value = value
    newInputs[key].error = InputValidate(newInputs[key])

    if (isAssignorEntityNameKey(key)) {
      const index = Number(key.replace('assignorEntityName', ''))
      const sourceOverridden = isOverrideEnabled(newInputs[assignorOverrideKey(index)]?.value)
      if (!sourceOverridden) {
        for (const i of ASSIGNOR_INDEXES) {
          const fieldKey = assignorEntityNameKey(i)
          if (fieldKey === key || !newInputs[fieldKey]) continue
          if (isOverrideEnabled(newInputs[assignorOverrideKey(i)]?.value)) continue
          newInputs[fieldKey].value = value
          newInputs[fieldKey].error = InputValidate(newInputs[fieldKey])
        }
        if (value !== inputs[key]?.value) fillAssignorAddress(newInputs, value)
      }
    }

    setInputs(newInputs)
  }

  const onToggleAssignorOverride = useCallback((fieldKey: string) => {
    if (!isAssignorEntityNameKey(fieldKey)) return
    const index = Number(fieldKey.replace('assignorEntityName', ''))
    const ovKey = assignorOverrideKey(index)

    setInputs((prev) => {
      const newInputs = cloneDeep(prev)
      const currentlyOverridden = isOverrideEnabled(newInputs[ovKey]?.value)
      if (currentlyOverridden) {
        const linkedValue =
          ASSIGNOR_INDEXES.filter((i) => i !== index && !isOverrideEnabled(newInputs[assignorOverrideKey(i)]?.value))
            .map((i) => newInputs[assignorEntityNameKey(i)]?.value)
            .find((value) => !!value) ||
          ASSIGNOR_INDEXES.filter((i) => i !== index)
            .map((i) => newInputs[assignorEntityNameKey(i)]?.value)
            .find((value) => !!value) ||
          newInputs[fieldKey].value ||
          ''
        newInputs[ovKey].value = false
        newInputs[fieldKey].inputType = 'select'
        newInputs[fieldKey].value = linkedValue
        newInputs[fieldKey].error = InputValidate(newInputs[fieldKey])
        const address = addressForAssignor(assignmentNamesRef.current, linkedValue)
        if (newInputs.assignorAddress && address) {
          newInputs.assignorAddress.value = address
          newInputs.assignorAddress.error = InputValidate(newInputs.assignorAddress)
        }
      } else {
        newInputs[ovKey].value = true
        newInputs[fieldKey].inputType = 'text'
        newInputs[fieldKey].type = 'text'
        newInputs[fieldKey].error = InputValidate(newInputs[fieldKey])
      }
      return newInputs
    })
  }, [])

  const inputsWithOverrideUi = useMemo(() => {
    const result = { ...inputs }
    for (const index of ASSIGNOR_INDEXES) {
      const key = assignorEntityNameKey(index)
      const ovKey = assignorOverrideKey(index)
      const isOv = isOverrideEnabled(inputs[ovKey]?.value)
      result[key] = {
        ...inputs[key],
        inputType: isOv ? 'text' : 'select',
        type: isOv ? 'text' : inputs[key].type,
        additionalElements: (
          <button
            type="button"
            className={`ml-0.5 p-0.5 rounded ${
              isOv ? 'text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-shade-blue hover:bg-blue-50'
            }`}
            title={isOv ? 'Clear override (re-link)' : 'Override (type a custom name)'}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onToggleAssignorOverride(key)
            }}
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
          </button>
        ),
        className: isOv ? 'bg-red-50' : '',
      } as typeof inputs[typeof key]
    }
    return result
  }, [inputs, onToggleAssignorOverride])

  const onGenerate = async (type: number) => {
    const newInputs = cloneDeep(inputs)
    const values: Record<string, any> = {}
    let hasError = false
    Object.keys(newInputs).forEach((key) => {
      const { value } = newInputs[key]
      // if (error) hasError = true
      values[key] = value
    })
    if (values.legalType == 'attach' && !values.legalFile) {
      newInputs.legalFile.error = REQUIRED_FIELD_ERROR_MESSAGE
      hasError = true
      setInputs(newInputs)
    }
    if (hasError) {
      toast(INVALID_ALL_INPUTS, { type: 'error' })
      return
    }

    const result = await confirmGenerate('Assignment')
    if (!result) return

    props.setLoading(true)
    setDownload(type)

    if (values.legalFile instanceof File) {
      const loanNumber = Api.getLoanNumber()
      const data = {
        path: `loan/${loanNumber}/postClosing`,
      }
      const [legalFileKey] = await uploadFiles(data, [values.legalFile])
      values.legalFile = legalFileKey
    }

    values.result = result
    values.type = type
    const res = await submitPostClosingPDF('assignment', values)
    if (result !== 'email') downloadFile(`Assignment - ${values.loanNumber}.pdf`, res)
    toast('Successfully completed', { type: 'success' })
    props.setLoading(false)
    setDownload(-1)
  }

  const AssignmentComponent = assignmentComponents[assignmentType]

  return (
    <div>
      <div className="flex items-center gap-1 mb-2">
        <h2 className="text-2xl font-bold">
          <span>Assignment</span>
        </h2>
        <AssignmentPdfSettingsModal
          assignmentNames={assignmentNames}
          onOk={(values) => onUpdateAssignmentNames(values)}
        />
      </div>
      <ButtonGroup
        title={{ ...assignmentTypeOptions }}
        value={assignmentType}
        onChange={(value) => setAssignmentType(value)}
      />

      <div>
        {AssignmentComponent && (
          <AssignmentComponent
            inputs={inputsWithOverrideUi}
            inputGroups={inputGroups}
            onChange={onChange}
            onGenerate={onGenerate}
            download={download}
          />
        )}
      </div>
    </div>
  )
}
